import React from 'react';
import './VACDashboard.css';

const VACDashboard = ({ result, onRecalculate, onDeepAnalysis }) => {
  if (!result) {
    return (
      <div className="vac-dashboard empty">
        <p>No valuation data available.</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  const getRiskCategoryColor = (category) => {
    switch (category) {
      case 'LOW':
        return '#10b981';
      case 'MEDIUM':
        return '#f59e0b';
      case 'HIGH':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getRiskCategoryLabel = (category) => {
    switch (category) {
      case 'LOW':
        return 'Low Risk';
      case 'MEDIUM':
        return 'Medium Risk';
      case 'HIGH':
        return 'High Risk';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="vac-dashboard">
      {/* Header with Current Value */}
      <div className="dashboard-header">
        <div className="current-value-card">
          <span className="label">Current Enterprise Value</span>
          <span className="value">{formatCurrency(result.currentValue)}</span>
          <span className="subtext">
            {formatCurrency(result.ebitdaAdjusted)} EBITDA × {result.multipleUsed?.toFixed(2)}x multiple
          </span>
        </div>

        {result.uplift && (
          <div className="potential-value-card">
            <span className="label">Potential Value (with improvements)</span>
            <span className="value">{formatCurrency(result.uplift.newTotalValue)}</span>
            <span className="uplift">
              +{formatCurrency(result.uplift.totalIncrease)} ({formatPercent(result.uplift.totalIncreasePercent)})
            </span>
          </div>
        )}
      </div>

      {/* Risk Score Section */}
      <div className="dashboard-section risk-section">
        <h2>Risk Assessment</h2>
        <div className="risk-score-display">
          <div className="risk-gauge">
            <svg viewBox="0 0 100 50" className="risk-gauge-svg">
              <path
                d="M 10 45 A 35 35 0 0 1 90 45"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="8"
              />
              <path
                d="M 10 45 A 35 35 0 0 1 90 45"
                fill="none"
                stroke={getRiskCategoryColor(result.riskCategory)}
                strokeWidth="8"
                strokeDasharray={`${(result.riskScore / 70) * 126} 126`}
              />
            </svg>
            <div className="risk-score-value">
              <span className="score">{result.riskScore}</span>
              <span className="max">/ 70</span>
            </div>
          </div>
          <div className="risk-category" style={{ color: getRiskCategoryColor(result.riskCategory) }}>
            {getRiskCategoryLabel(result.riskCategory)}
          </div>
          <p className="risk-description">
            {result.riskCategory === 'LOW' && 'Your business shows strong fundamentals with low acquisition risk. This qualifies for a premium multiple.'}
            {result.riskCategory === 'MEDIUM' && 'Your business has moderate risk factors. Addressing key issues could improve your multiple.'}
            {result.riskCategory === 'HIGH' && 'Several risk factors are impacting your valuation. Focus on reducing key risks to improve value.'}
          </p>
        </div>
      </div>

      {/* Value Acceleration Levers */}
      {result.uplift && (
        <div className="dashboard-section levers-section">
          <h2>Value Acceleration Levers</h2>
          <p className="section-description">
            Three strategies to increase your business value
          </p>

          <div className="levers-grid">
            {/* Size Lever */}
            <div className="lever-card size">
              <div className="lever-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 6l-9.5 9.5-5-5L1 18" />
                  <path d="M17 6h6v6" />
                </svg>
              </div>
              <h3>Size (Revenue Growth)</h3>
              <div className="lever-value">
                <span className="increase">+{formatCurrency(result.uplift.size.value)}</span>
                <span className="percent">+{formatPercent(result.uplift.size.percent)}</span>
              </div>
              <p className="lever-description">
                Growing revenue increases EBITDA proportionally, directly boosting enterprise value.
              </p>
              <div className="lever-target">
                Target: {formatPercent(result.uplift.size.target || 0.25)} revenue growth
              </div>
            </div>

            {/* Efficiency Lever */}
            <div className="lever-card efficiency">
              <div className="lever-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <h3>Efficiency (Margin Improvement)</h3>
              <div className="lever-value">
                <span className="increase">+{formatCurrency(result.uplift.efficiency.value)}</span>
                <span className="percent">+{formatPercent(result.uplift.efficiency.percent)}</span>
              </div>
              <p className="lever-description">
                Improving profit margins increases EBITDA without needing revenue growth.
              </p>
              <div className="lever-target">
                Target: {formatPercent(result.uplift.efficiency.target || 0.05)} margin improvement
              </div>
            </div>

            {/* Multiple Lever */}
            <div className="lever-card multiple">
              <div className="lever-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <h3>Multiple (Risk Reduction)</h3>
              <div className="lever-value">
                <span className="increase">+{formatCurrency(result.uplift.multiple.value)}</span>
                <span className="percent">+{formatPercent(result.uplift.multiple.percent)}</span>
              </div>
              <p className="lever-description">
                Reducing business risk increases the multiple buyers will pay for your EBITDA.
              </p>
              <div className="lever-target">
                Target: +{(result.uplift.multiple.target || 1.0).toFixed(1)}x multiple increase
              </div>
            </div>
          </div>

          {/* Combined Impact */}
          <div className="combined-impact">
            <div className="impact-bar">
              <div className="bar-segment current" style={{ width: `${(result.currentValue / result.uplift.newTotalValue) * 100}%` }}>
                <span>Current</span>
              </div>
              <div className="bar-segment size" style={{ width: `${(result.uplift.size.value / result.uplift.newTotalValue) * 100}%` }}>
                <span>Size</span>
              </div>
              <div className="bar-segment efficiency" style={{ width: `${(result.uplift.efficiency.value / result.uplift.newTotalValue) * 100}%` }}>
                <span>Efficiency</span>
              </div>
              <div className="bar-segment multiple" style={{ width: `${(result.uplift.multiple.value / result.uplift.newTotalValue) * 100}%` }}>
                <span>Multiple</span>
              </div>
            </div>
            <div className="impact-legend">
              <span className="legend-item current">Current Value</span>
              <span className="legend-item size">Size Uplift</span>
              <span className="legend-item efficiency">Efficiency Uplift</span>
              <span className="legend-item multiple">Multiple Uplift</span>
            </div>
          </div>
        </div>
      )}

      {/* Probability Distribution */}
      {result.probabilityDistribution && result.probabilityDistribution.length > 0 && (
        <div className="dashboard-section probability-section">
          <h2>Valuation Range</h2>
          <p className="section-description">
            Expected value range based on market conditions and risk factors
          </p>

          <div className="probability-chart">
            {result.probabilityDistribution.map((band, index) => (
              <div
                key={index}
                className="probability-band"
                style={{ height: `${band.probability * 200}px` }}
              >
                <div className="band-bar"></div>
                <div className="band-label">
                  <span className="band-value">{formatCurrency(band.value)}</span>
                  <span className="band-prob">{formatPercent(band.probability)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="expected-value">
            <span className="label">Expected Value:</span>
            <span className="value">{formatCurrency(result.expectedValue)}</span>
          </div>

          {result.valuationRange && (
            <div className="value-range">
              <span className="range-low">{formatCurrency(result.valuationRange.low)}</span>
              <span className="range-label">to</span>
              <span className="range-high">{formatCurrency(result.valuationRange.high)}</span>
            </div>
          )}
        </div>
      )}

      {/* Wealth Gap */}
      {result.wealthGap && result.wealthGap.targetValue && (
        <div className="dashboard-section wealth-gap-section">
          <h2>Wealth Gap Analysis</h2>
          <p className="section-description">
            Timeline to reach your target exit value
          </p>

          <div className="wealth-gap-summary">
            <div className="gap-metric">
              <span className="label">Current Value</span>
              <span className="value">{formatCurrency(result.currentValue)}</span>
            </div>
            <div className="gap-arrow">→</div>
            <div className="gap-metric target">
              <span className="label">Target Value</span>
              <span className="value">{formatCurrency(result.wealthGap.targetValue)}</span>
            </div>
          </div>

          <div className="wealth-gap-bar">
            <div
              className="gap-progress"
              style={{ width: `${Math.min((result.currentValue / result.wealthGap.targetValue) * 100, 100)}%` }}
            ></div>
            <span className="gap-percent">
              {((result.currentValue / result.wealthGap.targetValue) * 100).toFixed(1)}% of target
            </span>
          </div>

          {result.wealthGap.yearsToExit !== null && result.wealthGap.yearsToExit !== undefined && (
            <div className="years-to-exit">
              <span className="years">{result.wealthGap.yearsToExit.toFixed(1)}</span>
              <span className="label">years to reach target at current growth rate</span>
            </div>
          )}

          {result.wealthGap.yearlyTable && result.wealthGap.yearlyTable.length > 0 && (
            <div className="projection-table">
              <h3>Year-by-Year Projection</h3>
              <table>
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Projected Value</th>
                    <th>Gap to Target</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {result.wealthGap.yearlyTable.slice(0, 10).map((row, index) => (
                    <tr key={index} className={row.projectedValue >= result.wealthGap.targetValue ? 'target-reached' : ''}>
                      <td>{row.year}</td>
                      <td>{formatCurrency(row.projectedValue)}</td>
                      <td>{formatCurrency(row.gap)}</td>
                      <td>{formatPercent(row.percentToTarget / 100)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="dashboard-actions">
        {onRecalculate && (
          <button className="btn btn-secondary" onClick={onRecalculate}>
            Update Assessment
          </button>
        )}
        {onDeepAnalysis && (
          <button className="btn btn-success" onClick={onDeepAnalysis}>
            Exit Planning Analysis
          </button>
        )}
        <button className="btn btn-primary" onClick={() => window.print()}>
          Export Report
        </button>
      </div>
    </div>
  );
};

export default VACDashboard;
